import React, { forwardRef, PropsWithChildren, ReactNode } from "react"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { AnimatePresence, motion } from "framer-motion"
import styled from "styled-components"
import { theme } from "../../../theme"
import { Heading2 } from "../../atoms/text"

export type AccordionProps = {
  title: string
  open: boolean
  value: ReactNode
  completed: boolean
  animationTransitionDuration?: number
  action?: ReactNode
  className?: string
}

export const Accordion = forwardRef<HTMLDivElement, PropsWithChildren<AccordionProps>>((props, ref) => {
  const { title, open, value, animationTransitionDuration = 0.8, action, completed, children, className } = props

  return (
    <AccordionRoot className={className} ref={ref}>
      <AccordionTitleContainer>
        <AccordionTitleText $enabled={open} $completed={completed}>
          {title}
        </AccordionTitleText>
        <AccordionTitleRightSide>
          {!!value && value}
          {!!action && action}
          <CheckCircleIcon color={completed ? "success" : "disabled"} />
        </AccordionTitleRightSide>
      </AccordionTitleContainer>
      <AnimatePresence initial={false}>
        {open && !!children && (
          <motion.div
            style={{ overflow: "hidden" }}
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: animationTransitionDuration }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </AccordionRoot>
  )
})

const AccordionRoot = styled.div`
  border-bottom: 1px solid ${theme.color.white2};
  &:last-child {
    border-bottom: none;
  }
`
const AccordionTitleContainer = styled(motion.div)`
  background-color: white;
  height: 72px;
  padding: ${theme.spacing.lx};
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const AccordionTitleText = styled(Heading2)<{ $enabled: boolean; $completed: boolean }>`
  color: ${(p) => (p.$enabled || p.$completed ? theme.color.black6 : theme.color.grey2)};
`
const AccordionTitleRightSide = styled.div`
  display: flex;
  gap: ${theme.spacing.m};
`
